import { useCallback, useState, useEffect, KeyboardEvent, memo } from "react";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import { Dropdown, Row, Col, Button } from "react-bootstrap";
import { DateTime } from "luxon";
import Papa from "papaparse";

import CampTransactionTable from "../components/CampTransactionTable";
import DateRangePicker from "../components/DateRangePicker";
import PaginationComponent from "../components/Pagination";
import { useRegion } from "../contexts/RegionContext";
import {
  getCampTransactions,
  CampTransactionSearchQuery,
} from "../services/campTransactionServices";
import {
  CampTransactionsType,
  CampTransactionStatus,
} from "../types/campTransaction";
import { PAGE_SIZE_DEFAULT } from "../constants";

import "../components/Search/index.scss";
import styles from "../styles/campTransactions.module.scss";

enum SearchByEnum {
  TRANSACTION_ID = "Transaction ID",
  TRADE_DATE = "Trade Date",
  SETTLEMENT_DATE = "Settlement Date",
}

const TRANSACTION_STATUSES: CampTransactionStatus[] = ["PENDING", "ACTUAL"];

const toISODate = (d: Date | null) =>
  d ? DateTime.fromJSDate(d).toFormat("yyyy-MM-dd") : undefined;

// --- Sub-components ---

const CampPageHeader = ({
  onDownload,
  canDownload,
}: {
  onDownload: () => void;
  canDownload: boolean;
}) => (
  <div className={styles.pageHeader}>
    <h1 className={styles.pageTitle}>CAMP Activity</h1>
    <Button
      className={styles.actionBtn}
      data-testid="camp-download-btn"
      disabled={!canDownload}
      onClick={onDownload}
    >
      <span className="material-icons material-icons-outlined me-1">
        download
      </span>
      Download
    </Button>
  </div>
);

const SearchTypeDropdown = ({
  searchBy,
  setSearchBy,
}: {
  searchBy: SearchByEnum;
  setSearchBy: (v: SearchByEnum) => void;
}) => (
  <Dropdown className="search-dropdown" style={{ width: "fit-content" }}>
    <Dropdown.Toggle
      variant="primary"
      className="search-toggle"
      data-testid="camp-select-search-type"
      style={{ width: "fit-content" }}
    >
      <span>{searchBy}</span>
      <span className="material-icons material-icons-outlined">
        chevron_right
      </span>
    </Dropdown.Toggle>
    <Dropdown.Menu>
      {Object.values(SearchByEnum).map((opt) => (
        <Dropdown.Item key={opt} onClick={() => setSearchBy(opt)}>
          {opt}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

const StatusFilterDropdown = ({
  statusFilter,
  setStatusFilter,
}: {
  statusFilter: CampTransactionStatus | null;
  setStatusFilter: (v: CampTransactionStatus | null) => void;
}) => (
  <Dropdown className="add-filters-dropdown m-0">
    <Dropdown.Toggle
      variant="primary"
      className="search-toggle"
      data-testid="camp-status-filter-dropdown"
    >
      <span className={statusFilter ? "" : "title-text"}>
        {statusFilter ?? "Transaction Status"}
      </span>
      <span className="material-icons material-icons-outlined">
        chevron_right
      </span>
    </Dropdown.Toggle>
    <Dropdown.Menu>
      {TRANSACTION_STATUSES.map((s) => (
        <Dropdown.Item key={s} onClick={() => setStatusFilter(s)}>
          {s}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

const UserIdFilterInput = ({
  userIdFilter,
  setUserIdFilter,
  onKeyDown,
}: {
  userIdFilter: string;
  setUserIdFilter: (v: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}) => (
  <div className={`search-input ${styles.userIdFilterInput}`}>
    <span className="search-icon material-icons material-icons-outlined">
      person
    </span>
    <input
      data-testid="camp-user-id-filter"
      type="text"
      className="form-control p-0 bg-transparent"
      placeholder="User ID"
      value={userIdFilter}
      onChange={(e) => setUserIdFilter(e.target.value)}
      onKeyDown={onKeyDown}
    />
  </div>
);

type SearchInputRowProps = {
  searchBy: SearchByEnum;
  setSearchBy: (v: SearchByEnum) => void;
  searchText: string;
  setSearchText: (v: string) => void;
  dateFrom: Date | null;
  dateTo: Date | null;
  onDateChange: (start: Date | null, end: Date | null) => void;
  onSearch: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
};

const SearchInputRow = ({
  searchBy,
  setSearchBy,
  searchText,
  setSearchText,
  dateFrom,
  dateTo,
  onDateChange,
  onSearch,
  onKeyDown,
}: SearchInputRowProps) => {
  const isDateSearch =
    searchBy === SearchByEnum.TRADE_DATE ||
    searchBy === SearchByEnum.SETTLEMENT_DATE;

  return (
    <Row className="nowrap align-items-md-center m-0 spcace-x-2">
      <Col className="d-flex align-items-stretch p-0">
        <SearchTypeDropdown searchBy={searchBy} setSearchBy={setSearchBy} />
        {isDateSearch ? (
          <Row
            className={`nowrap search-input align-items-md-center p-1 ${styles.dateSearchInput}`}
          >
            <DateRangePicker
              startDate={dateFrom}
              endDate={dateTo}
              onChange={onDateChange}
              placeholderText="Select date range"
            />
          </Row>
        ) : (
          <div className={`search-input ${styles.searchInputRow}`}>
            <span className="search-icon material-icons material-icons-outlined">
              search
            </span>
            <input
              data-testid="camp-search-input"
              type="text"
              className="form-control p-0 bg-transparent"
              placeholder="Enter search term"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={onKeyDown}
            />
          </div>
        )}
      </Col>
      <Col>
        <Button
          className="search-btn"
          data-testid="camp-search-btn"
          variant="success"
          size="lg"
          onClick={onSearch}
        >
          Search
        </Button>
      </Col>
    </Row>
  );
};

type CampSearchSectionProps = SearchInputRowProps & {
  statusFilter: CampTransactionStatus | null;
  setStatusFilter: (v: CampTransactionStatus | null) => void;
  userIdFilter: string;
  setUserIdFilter: (v: string) => void;
  hasActiveSearch: boolean;
  totalCount?: number;
  onClear: () => void;
};

const CampSearchSection = ({
  searchBy,
  setSearchBy,
  searchText,
  setSearchText,
  dateFrom,
  dateTo,
  onDateChange,
  onSearch,
  onKeyDown,
  statusFilter,
  setStatusFilter,
  userIdFilter,
  setUserIdFilter,
  hasActiveSearch,
  totalCount,
  onClear,
}: CampSearchSectionProps) => (
  <Col>
    <div className="d-flex">
      <div>
        <div className="mb-1 title-text">Search by:</div>
        <SearchInputRow
          searchBy={searchBy}
          setSearchBy={setSearchBy}
          searchText={searchText}
          setSearchText={setSearchText}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateChange={onDateChange}
          onSearch={onSearch}
          onKeyDown={onKeyDown}
        />
      </div>
      <div className="filter-container">
        <div className="mb-1 title-text">Filter by:</div>
        <div className={styles.filterRow}>
          <StatusFilterDropdown
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
          />
          <UserIdFilterInput
            userIdFilter={userIdFilter}
            setUserIdFilter={setUserIdFilter}
            onKeyDown={onKeyDown}
          />
        </div>
      </div>
    </div>
    {hasActiveSearch && (
      <>
        <hr className="divider" />
        <Row
          className="nowrap search-result align-items-md-center"
          data-testid="camp-search-result"
        >
          <span>
            {totalCount ?? 0} {totalCount === 1 ? "result" : "results"} found
          </span>
          <Row
            className="nowrap flex-full align-items-md-center clear-btn"
            onClick={onClear}
            data-testid="camp-clear-btn"
          >
            <span className="material-icons material-icons-outlined">
              close
            </span>
            <span>Clear search or filters</span>
          </Row>
        </Row>
      </>
    )}
  </Col>
);

// --- Main component ---

const CampTransactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get("userId")?.trim() ?? "";

  const [searchBy, setSearchBy] = useState<SearchByEnum>(
    SearchByEnum.TRANSACTION_ID
  );
  const [searchText, setSearchText] = useState("");
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [statusFilter, setStatusFilter] =
    useState<CampTransactionStatus | null>(null);
  const [userIdFilter, setUserIdFilter] = useState(userIdFromUrl);
  const [searchQuery, setSearchQuery] = useState<CampTransactionSearchQuery>();
  const [currentPage, setCurrentPage] = useState(1);
  const { selectedRegion } = useRegion();

  useEffect(() => {
    setSearchText("");
    setDateFrom(null);
    setDateTo(null);
  }, [searchBy]);

  const buildQuery = useCallback(
    (
      by: SearchByEnum,
      text: string,
      from: Date | null,
      to: Date | null,
      status: CampTransactionStatus | null,
      userId: string,
      appCountry: string | null
    ): CampTransactionSearchQuery | undefined => {
      const query: CampTransactionSearchQuery = {};
      if (by === SearchByEnum.TRANSACTION_ID && text) {
        query.transactionId = text;
      } else if (by === SearchByEnum.TRADE_DATE) {
        if (from) query.tradeDateFrom = toISODate(from);
        if (to) query.tradeDateTo = toISODate(to);
      } else if (by === SearchByEnum.SETTLEMENT_DATE) {
        if (from) query.settlementDateFrom = toISODate(from);
        if (to) query.settlementDateTo = toISODate(to);
      }
      if (status) query.transactionStatus = status;
      if (userId.trim()) query.userId = userId.trim();
      if (appCountry) query.appCountry = appCountry;
      return Object.keys(query).length > 0 ? query : undefined;
    },
    []
  );

  const onClickSearch = useCallback(() => {
    setCurrentPage(1);
    setSearchQuery(
      buildQuery(
        searchBy,
        searchText,
        dateFrom,
        dateTo,
        statusFilter,
        userIdFilter,
        selectedRegion
      )
    );
  }, [
    searchBy,
    searchText,
    dateFrom,
    dateTo,
    statusFilter,
    userIdFilter,
    selectedRegion,
    buildQuery,
  ]);

  const clearAll = useCallback(() => {
    setSearchText("");
    setDateFrom(null);
    setDateTo(null);
    setStatusFilter(null);
    setUserIdFilter("");
    setCurrentPage(1);
    setSearchQuery(
      buildQuery(searchBy, "", null, null, null, "", selectedRegion)
    );
    if (searchParams.has("userId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("userId");
      setSearchParams(next, { replace: true });
    }
  }, [searchBy, selectedRegion, buildQuery, searchParams, setSearchParams]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") onClickSearch();
  };

  // Apply userId from the URL (e.g. deep link from the user detail page) and
  // auto-run the search so matching transactions load immediately.
  useEffect(() => {
    if (!userIdFromUrl) return;
    setUserIdFilter(userIdFromUrl);
    setCurrentPage(1);
    setSearchQuery(
      buildQuery(
        searchBy,
        searchText,
        dateFrom,
        dateTo,
        statusFilter,
        userIdFromUrl,
        selectedRegion
      )
    );
    // Only re-run when the URL userId changes; form values are sampled then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdFromUrl, buildQuery]);

  // Rebuild from current form state when region changes (may apply uncommitted
  // search/date/status values — same as treating region like any other filter input).
  useEffect(() => {
    setCurrentPage(1);
    setSearchQuery(
      buildQuery(
        searchBy,
        searchText,
        dateFrom,
        dateTo,
        statusFilter,
        userIdFilter,
        selectedRegion
      )
    );
    // Only re-run on region change; form values are sampled at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegion, buildQuery]);

  const { isLoading, error, data } = useQuery<CampTransactionsType, Error>(
    ["campTransactions", currentPage, searchQuery],
    async () =>
      await getCampTransactions({
        page: currentPage - 1,
        pageSize: PAGE_SIZE_DEFAULT,
        searchQuery,
      })
  );

  const handleDownload = useCallback(async () => {
    const totalCount = data?.pageInfo?.totalCount;
    if (!totalCount) return;
    const allData = await getCampTransactions({
      page: 0,
      pageSize: totalCount,
      searchQuery,
    });
    const csv = Papa.unparse(allData.transactions);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `camp-transactions-${DateTime.now().toFormat(
      "yyyy-MM-dd"
    )}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [data, searchQuery]);

  const hasActiveSearch =
    searchQuery && Object.keys(searchQuery).some((key) => key !== "appCountry");
  const canDownload =
    Boolean(data?.pageInfo) && (data?.pageInfo?.totalCount ?? 0) <= 100;

  return (
    <div>
      <CampPageHeader onDownload={handleDownload} canDownload={canDownload} />
      <CampSearchSection
        searchBy={searchBy}
        setSearchBy={setSearchBy}
        searchText={searchText}
        setSearchText={setSearchText}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateChange={(start, end) => {
          setDateFrom(start);
          setDateTo(end);
        }}
        onSearch={onClickSearch}
        onKeyDown={handleKeyDown}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        userIdFilter={userIdFilter}
        setUserIdFilter={setUserIdFilter}
        hasActiveSearch={Boolean(hasActiveSearch)}
        totalCount={data?.pageInfo?.totalCount}
        onClear={clearAll}
      />
      <div className={styles.container}>
        {data?.pageInfo && (
          <PaginationComponent
            pageCount={Math.ceil(
              data.pageInfo.totalCount / data.pageInfo.pageSize
            )}
            currentPage={currentPage}
            itemOfPage={data.transactions.length}
            totalItems={data.pageInfo.totalCount}
            containerClass={styles.tableTopPagination}
            setCurrentPage={setCurrentPage}
          />
        )}
        <CampTransactionTable data={data} error={error} isLoading={isLoading} />
        {data?.pageInfo && (
          <PaginationComponent
            pageCount={Math.ceil(
              data.pageInfo.totalCount / data.pageInfo.pageSize
            )}
            currentPage={currentPage}
            itemOfPage={data.transactions.length}
            totalItems={data.pageInfo.totalCount}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default memo(CampTransactions);
